import { Alert, Box, CircularProgress, Paper, Stack } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MethodConfig } from "../../../config/Methods.config";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { MethodsInput, UpdateMethodsInputRequest } from "../../../types/model/methodsInput";
import FormAutocomplete from "../../General/FormAutocomplete";
import CreateSheetDialog from "./CreateSheetDialog";
import MethodSummaryCard from "./MethodSummaryCard";
import { NEW_SHEET_OPTION_ID, getFieldKeys, normalizeFieldValue } from "./MethodRunner.utils";

interface MethodProps<TInputs> {
  captureId: number;
  methodsInputs: MethodsInput[];
  canEdit: boolean;
  config: MethodConfig<TInputs>;
  onUpdate: (inputId: number, values: UpdateMethodsInputRequest) => Promise<void>;
  onCreate: (values: { name: string }) => Promise<MethodsInput>;
}

interface FormValues {
  selectedInputId: number | "";
}

export default function MethodRunner<TInputs>({
  captureId,
  methodsInputs,
  canEdit,
  config,
  onUpdate,
  onCreate,
}: Readonly<MethodProps<TInputs>>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(false);

  const hasExtraInputs = !!config.ExtraInputsComponent;
  const inputs = config.inputs ?? [];

  const [loading, setLoading] = useState(false);
  const [currentCalculations, setCurrentCalculations] = useState<any[]>([]);
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [creatingSheet, setCreatingSheet] = useState(false);

  const methods = useForm<FormValues>({
    defaultValues: { selectedInputId: "" },
    shouldFocusError: false,
  });
  const summaryForm = useForm<Record<string, unknown>>();

  const selectedInputId = methods.watch("selectedInputId");
  const selectedInput = methodsInputs.find((i) => i.id === selectedInputId);

  const summaryRef = useRef<HTMLDivElement | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const justCreatedRef = useRef(false);

  useEffect(() => {
    setCurrentCalculations([]);
  }, [selectedInputId]);

  useEffect(() => {
    if (selectedInputId === NEW_SHEET_OPTION_ID) {
      setShowCreateDialog(true);
      return;
    }

    if (selectedInput) {
      summaryForm.reset(selectedInput as any);
    }

    setIsEditingSummary(justCreatedRef.current);
    justCreatedRef.current = false;
  }, [selectedInputId]);

  useEffect(() => {
    fetchResults();
  }, [selectedInputId]);

  useEffect(() => {
    if (!loading && selectedInput && summaryRef.current) {
      summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, selectedInput]);

  useEffect(() => {
    if (!loading && currentCalculations.length > 0 && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading, currentCalculations]);

  const fetchResults = async () => {
    if (!selectedInputId || selectedInputId === NEW_SHEET_OPTION_ID) return;

    setLoading(true);

    try {
      try {
        const res = await config.getResults(captureId, selectedInputId);

        if (Array.isArray(res) && res.length > 0) {
          setCurrentCalculations(res);

          if (hasExtraInputs && res[0]?.inputParameters && config.populateExtraInputs) {
            config.populateExtraInputs(res[0], methods.setValue);
          }

          return;
        }
      } catch (error) {
        handleError(error);
      }

      if (canEdit && !hasExtraInputs) {
        await config.calculate(captureId, selectedInputId);
        const newRes = await config.getResults(captureId, selectedInputId);
        setCurrentCalculations(newRes ?? []);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const runCalculation = async () => {
    let payload: any;

    if (config.ExtraInputsComponent) {
      const extraValues = methods.getValues();
      const extraKeys = Object.entries(extraValues).flatMap(([key, value]) => getFieldKeys(key, value));
      const valid = await methods.trigger(extraKeys as any, { shouldFocus: false });

      if (!valid) return false;

      payload = config.extraInputsSchema ? config.extraInputsSchema.parse(extraValues) : undefined;
    }

    await config.calculate(captureId, selectedInputId as number, payload);
    const newRes = await config.getResults(captureId, selectedInputId as number);
    setCurrentCalculations(newRes ?? []);
    return true;
  };

  const handleCalculate = async () => {
    if (!selectedInputId || selectedInputId === NEW_SHEET_OPTION_ID) return;

    setLoading(true);
    try {
      await runCalculation();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSheet = async () => {
    const name = newSheetName.trim();
    if (!name) return;

    setCreatingSheet(true);

    try {
      const created = await onCreate({ name });

      setShowCreateDialog(false);
      setNewSheetName("");
      justCreatedRef.current = true;
      methods.setValue("selectedInputId", created.id);
    } catch (error) {
      handleError(error);
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleCancelCreateSheet = () => {
    setShowCreateDialog(false);
    setNewSheetName("");
    methods.setValue("selectedInputId", "");
  };

  const handleSaveSummary = async () => {
    if (!selectedInputId || !selectedInput) return;

    setSavingSummary(true);

    try {
      const allValues = summaryForm.getValues();

      const editedFields = inputs.reduce((acc, field) => {
        const fieldName = String(field);
        const originalValue = selectedInput[field as keyof typeof selectedInput];
        acc[fieldName] = normalizeFieldValue(fieldName, allValues[fieldName], originalValue);
        return acc;
      }, {} as Record<string, unknown>);

      const values = { ...selectedInput, ...editedFields } as UpdateMethodsInputRequest;

      await onUpdate(selectedInputId, values);

      if (canEdit) {
        await runCalculation();
      } else {
        const newRes = await config.getResults(captureId, selectedInputId);
        setCurrentCalculations(newRes ?? []);
      }

      setIsEditingSummary(false);
    } catch (error) {
      handleError(error);
    } finally {
      setSavingSummary(false);
    }
  };

  const handleCancelSummary = () => {
    summaryForm.reset(selectedInput as any);
    setIsEditingSummary(false);
  };

  return (
    <FormProvider {...methods}>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ width: { xs: "100%", sm: 300 } }}>
            <FormAutocomplete
              name="selectedInputId"
              label=""
              options={[
                ...methodsInputs.map((mi) => ({ id: mi.id, name: mi.name ?? "" })),
                ...(canEdit ? [{ id: NEW_SHEET_OPTION_ID, name: `+ ${t("calculations.methods.new-sheet")}` }] : []),
              ]}
              isEditing
              isSaving={false}
              formStyles={formStyles}
            />

            {methodsInputs.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t("calculations.methods.no-sheets-alert")}
              </Alert>
            )}
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && selectedInput && (
            <MethodSummaryCard
              captureId={captureId}
              selectedInput={selectedInput}
              inputs={inputs}
              config={config}
              canEdit={canEdit}
              isEditingSummary={isEditingSummary}
              savingSummary={savingSummary}
              currentCalculations={currentCalculations}
              summaryControl={summaryForm.control}
              onEdit={() => setIsEditingSummary(true)}
              onCancelEdit={handleCancelSummary}
              onSave={handleSaveSummary}
              onCalculate={handleCalculate}
              summaryRef={summaryRef}
              resultsRef={resultsRef}
            />
          )}

          <CreateSheetDialog
            open={showCreateDialog}
            name={newSheetName}
            saving={creatingSheet}
            onNameChange={setNewSheetName}
            onConfirm={handleCreateSheet}
            onCancel={handleCancelCreateSheet}
          />
        </Stack>
      </Paper>
    </FormProvider>
  );
}